import { Accordion, AccordionDetails, AccordionSummary, List } from '@mui/material';
import { styled } from '@mui/material/styles';
import { router } from '~/router';
import Icon from 'Components/Icon';
import Tree from 'Components/Tree';
import { expandTree, toggleNode } from 'ducks/ui';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import sortDevices from 'utils/sortDevices';
import statusToColor from 'utils/statusToColor';
import { SPACER_MINI, TREE_HEADER_HEIGHT } from 'utils/ui/constants';
import Toolbar from './Toolbar';

const TreeCollapsed = styled('div')({
    marginTop: 0,
    width: '100%',
    height: TREE_HEADER_HEIGHT + SPACER_MINI
});

const TreeExpanded = styled('div')({
    marginTop: 0,
    width: '100%'
});

const StyledAccordionDetails = styled(AccordionDetails)({
    borderTop: '1px solid #DDDDDD',
    height: 'calc(100vh - 140px)',
    overflow: 'auto',
    padding: 0
});

const StyledAccordion = styled(Accordion)({
    cursor: 'pointer',
    '&:first-of-type': { borderRadius: 0 },
    '&:last-child': { borderRadius: 0 }
});

const StyledAccordionSummary = styled(AccordionSummary)({
    height: 36,
    margin: 0,
    padding: 0,
    minHeight: 36,
    '&.Mui-expanded': {
        minHeight: 36,
        margin: 0,
        padding: 0
    }
});

const StyledList = styled(List)({
    width: '100%'
});

const getPrimaryText = device => <span style={{ fontSize: 13 }}>{device.name}</span>;

const getSecondaryText = device => {
    let text = device.equipment_name;

    const attributeToName = { ip: 'IP', mbus_id: 'MBus', modbus_id: 'Modbus', one_wire_id: 'OneWire' };

    for (const [key, label] of Object.entries(attributeToName)) {
        const value = device.extra[key];
        if (value) {
            text = `${label} ${value} (${text})`;
            break;
        }
    }

    return (
        <>
            <Icon color={statusToColor(device.status)} icon={['fas', 'circle']} size="xs" style={{ marginRight: 5 }}/>
            <b style={{ fontSize: 12 }}>{text}</b>
        </>
    );
};

const reducer = (deviceIds, devices) => {
    return deviceIds
        .map(id => devices[id])
        .filter(device => device.parent_id === null || devices[device.parent_id]?.status === 'disabled' || device.status !== 'disabled')
        .sort(sortDevices)
        .map(device => device.id);
};

const Component = ({ siteId, deviceId }) => {
    const byParentId = useSelector(state => state.devices.byParentId);
    const devices = useSelector(state => state.devices.models);
    const openNodes = useSelector(state => state.ui.openNodes);
    const panelOpen = useSelector(state => state.ui.showSites);

    const rootDeviceIds = (byParentId[null] || []).reduce((acc, id) => {
        const device = devices[id] || {};
        if (device.site_id === siteId) {
            acc.push(device.id);
        }
        return acc;
    }, []);

    const dispatch = useDispatch();
    useEffect(() => {
        if (deviceId && devices[deviceId]) {
            dispatch(expandTree(deviceId, devices));
        }
        // re-expands when we land on a different device; `devices` changes identity on
        // every fetch, and depending on it would re-expand the tree under the user
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    const trees = reducer(rootDeviceIds, devices).map(rootDeviceId => (
        <Tree
            activeDeviceId={deviceId}
            getPrimaryText={getPrimaryText}
            getSecondaryText={getSecondaryText}
            indexedNodes={byParentId}
            key={rootDeviceId}
            nodeId={rootDeviceId}
            nodes={devices}
            openNodes={openNodes}
            selectNode={deviceId => router.navigate(`/devices/${deviceId}`)}
            sorter={reducer}
            toggleNode={id => dispatch(toggleNode(id))}
        />
    ));

    return (
        <>
            {panelOpen ? <TreeCollapsed/> : <TreeExpanded/>}
            <StyledAccordion disableGutters expanded={!panelOpen}>
                <StyledAccordionSummary component="div">
                    <Toolbar siteId={siteId}/>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                    <StyledList dense disablePadding>
                        {trees}
                    </StyledList>
                </StyledAccordionDetails>
            </StyledAccordion>
        </>
    );
};

Component.propTypes = {
    siteId: PropTypes.string.isRequired,
    deviceId: PropTypes.string
};

export default Component;
